import {useGrpcRequest, type CacheKeyConfig} from '@aboutyou/data-fetcher';
import {
  ArticleDetailService_GetRecommendationsLazy,
  ArticleDetailService_GetRecommendationsNextLazy,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.grpc.lazy';
import {useOptionalSession} from '@aboutyou/user';
import {useStaleLastSeenProductIds} from '../../../../features/LastSeenProducts/useLastSeenProductIds';
import {useProductIdFromSlug} from '../../../../features/ProductUrl';
import {useReductionsStateRequestParameter} from '../../../../features/ReductionsState/useReductionsStateRequestParameter';
import {useSelectedTargetGroup} from '../../../../shared-deprecated/useSelectedTargetGroup';
import {
  useKeepPreviousRecommendationsData,
  useUpdateKeepPreviousData,
} from './useKeepPreviousRecommendationsData';
import {EntrySource} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';

export const LANES_PER_PAGE = 1;

const UNIQUE_RECOMMENDATION_LAST_SEEN_PRODUCTS_KEY =
  'UNIQUE_RECOMMENDATION_LAST_SEEN_PRODUCTS_KEY';

const transformCacheKey = (config: CacheKeyConfig) => {
  return {
    ...config,
    params: {
      ...config.params,
      // Exclude lastSeenProductIds from cache key to prevent re-fetches when only this changes
      // The backend still receives this data for personalization
      lastSeenProductIds: undefined,
    },
  };
};

const getNextState = (items: Array<{continuationToken?: string}>) =>
  items[items.length - 1]?.continuationToken;

const useRecommendationsRequest = (limit: number | null) => {
  const productId = useProductIdFromSlug();
  const session = useOptionalSession();
  const {targetGroup} = useSelectedTargetGroup();
  const reductionsState = useReductionsStateRequestParameter();
  const lastSeenProductIds = useStaleLastSeenProductIds({
    uniqueKey: UNIQUE_RECOMMENDATION_LAST_SEEN_PRODUCTS_KEY,
  });

  const {keepPreviousData, updateKeepPreviousData} =
    useKeepPreviousRecommendationsData();

  const request = useGrpcRequest(
    ArticleDetailService_GetRecommendationsLazy,
    {
      productId: productId!,
      lastSeenProductIds,
      session,
      targetGroup,
      reductionsState,
      entrySource: EntrySource.UNSPECIFIED,
      limit,
    },
    {
      keepPreviousData,
      transformCacheKey,
    },
  );

  useUpdateKeepPreviousData(request, updateKeepPreviousData, productId);

  return {
    ...request,
    data: request.data
      ? {
          ...request.data,
          nextState: limit ? getNextState(request.data.items) : undefined,
        }
      : undefined,
  };
};

export const useRecommendationsData = () =>
  useRecommendationsRequest(LANES_PER_PAGE);

export const useAllRecommendationsData = () => useRecommendationsRequest(null);

export const useNextRecommendationsData = ({token}: {token: string}) => {
  const session = useOptionalSession();
  const reductionsState = useReductionsStateRequestParameter();

  const request = useGrpcRequest(
    ArticleDetailService_GetRecommendationsNextLazy,
    {
      session,
      reductionsState,
      continuationToken: token,
    },
  );

  return {
    ...request,
    data: request.data
      ? {...request.data, nextState: getNextState(request.data.items)}
      : undefined,
  };
};
